// src/api/queryClient.ts
import { QueryClient } from 'react-query';
import { redirectToLogin } from '../helpers/redirectToLogin';

const isAuthError = (error: unknown) => {
    return (
        error instanceof Error &&
        "response" in error &&
        (error as any).response?.status === 401
    );
};

const handleAuthError = () => {
    localStorage.removeItem("ACCESS_TOKEN");
    redirectToLogin();
};

/**
 * Les gardes d'authentification, partagées par les deux clients.
 *
 * Extraites pour que le client mobile les hérite sans dupliquer le fichier — la
 * seule chose qui diffère entre web et mobile est la RÉTENTION, pas la façon de
 * traiter un 401.
 */
const gardesCommunes = {
    onError: (error: unknown) => {
        if (isAuthError(error)) {
            handleAuthError();
        }
    },
};

/**
 * La rétention du mobile — décision #24, appliquée par #86.
 *
 * Le mobile montait jusqu'ici le client du WEB, avec ses 5 minutes. Sur un réseau
 * d'hôtel, cinq minutes en arrière-plan suffisent à vider le cache : l'app
 * revenait donc vide à chaque retour, y compris quand rien n'avait changé.
 *
 * Une heure : tant que l'app vit, ce qui a été lu reste lisible. **Rien n'est
 * persisté sur disque** — le contenu des messages est de la donnée client, et ne
 * rien écrire fait qu'il n'y a rien à purger à la déconnexion.
 *
 * ⚠️ Contrepartie non négociable, portée par `useAgeDeLaDonnee` : **la donnée
 * périmée dit son âge**. Sans ça on retombe sur le « zéro plausible et faux » que
 * #27 a refusé — une conversation de tout à l'heure présentée comme l'état
 * actuel. Le bandeau ne suffit pas : il disparaît au retour du réseau, là où
 * l'affichage peut rester périmé encore un moment.
 */
export const RETENTION_MOBILE_MS = 60 * 60 * 1000;

export const queryClientMobile = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 5 * 60 * 1000,
            cacheTime: RETENTION_MOBILE_MS,
            // Un seul réessai (audit 2026-09-23, A3) : les trois par défaut, chacun
            // jusqu'au délai d'abandon de 15 s, faisaient attendre une minute avant
            // de dire l'échec sur un réseau d'hôtel. Le web a sa propre règle
            // (shouldRetryWebQuery).
            retry: 1,
            ...gardesCommunes,
        },
        mutations: { ...gardesCommunes },
    },
});

/**
 * Réessai des requêtes web. Pas sur lenteur (abandon à 15 s, 504) : PHP poursuit le
 * calcul, chaque réessai en empile un de plus. Pas sur 4xx : même réponse. Une fois
 * sur 502/503 (passager, ex. déploiement — un polling dont le 1er fetch échoue ne
 * repart pas). Jusqu'à 3 fois sinon (coupure réseau, autre 5xx), comme avant.
 */
export function shouldRetryWebQuery(failureCount: number, error: unknown): boolean {
    const { code, response } = (error ?? {}) as { code?: string; response?: { status?: number } };
    const status = response?.status;

    if (code === 'ECONNABORTED' || code === 'ETIMEDOUT' || status === 504 || (status !== undefined && status < 500)) {
        return false;
    }

    return failureCount < (status === 502 || status === 503 ? 1 : 3);
}

export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 5 * 60 * 1000,
            cacheTime: 5 * 60 * 1000,
            retry: shouldRetryWebQuery,
            ...gardesCommunes,
        },
        mutations: { ...gardesCommunes },
    },
});
